import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import SearchIcon from '@mui/icons-material/Search';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { CircularProgress, LinearProgress } from '@mui/material';
import client from '../../util/api-client';
import type {
  Customer,
  CustomerPaymentCreateInput,
  CustomerPaymentGroupedSearchInput,
  CustomerPaymentInvoiceGroup,
  CustomerPaymentSearchCustomer,
} from '@repo/api-contract';
import { pushToast, useDispatch } from '../../state';
import { openSearchCustomerModal } from '../../state/customerSearchModalSlice';
import SelectSearchCustomer from '../../components/utils/SelectSearchCustomer';
import { topNavBarHeight } from '../../components/NavBars/constants';
import DraftPaymentsSidebar from './DraftPaymentsSidebar';

type CustomerPaymentDraftRow = CustomerPaymentCreateInput & {
  id: string;
  isNew: true;
};

type PaymentTableRow = {
  id: string;
  isNew: boolean;
  customerCode: string;
  companyName: string;
  invoiceCode: string;
  invoiceDate: number;
  openingBalance: number;
  payment: number;
  reference: string;
  adjustment: number;
  detail: string;
  captureDate: number;
  resultingBalance: number;
  groupInvoiceBalance?: number;
  groupResultingBalance?: number;
  isEmptyInvoice?: boolean;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatCurrency = (value: number | undefined) =>
  value !== undefined ? currencyFormatter.format(value) : '';

const formatDate = (value: number | undefined) =>
  value ? new Date(value * 1000).toLocaleDateString() : '';

const PAYMENT_GROUP_PAGE_SIZE = 15;

function InvoiceGroupActionButton({
  invoiceCode,
  onSelect,
}: {
  invoiceCode: string;
  onSelect: (invoiceCode: string) => void;
}) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PaymentReceived',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = () => {
    onSelect(invoiceCode);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-controls={open ? 'payment-received-invoice-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={t('Invoice actions')}
        size="small"
        onClick={handleOpen}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="payment-received-invoice-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'payment-received-invoice-actions',
        }}
      >
        <MenuItem onClick={handleSelect}>{t('Use invoice')}</MenuItem>
      </Menu>
    </>
  );
}

function PaymentRowActionButton({
  disabled,
  onDelete,
}: {
  disabled: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PaymentReceived',
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    onDelete();
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-controls={open ? 'payment-received-payment-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={t('Payment actions')}
        disabled={disabled}
        size="small"
        onClick={handleOpen}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id="payment-received-payment-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'payment-received-payment-actions',
        }}
      >
        <MenuItem
          disabled={disabled}
          onClick={handleDelete}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('Delete')}
        </MenuItem>
      </Menu>
    </>
  );
}

export default function PaymentReceived() {
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.PaymentReceived',
  });
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const expandedInvoiceCodesRef = React.useRef<Set<string>>(new Set());

  const [searchBy, setSearchBy] = useState<'customerCode' | 'invoiceCode'>(
    'customerCode',
  );
  const [searchInput, setSearchInput] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextAfterKey, setNextAfterKey] = useState<
    Record<string, string> | undefined
  >();
  const [groups, setGroups] = useState<CustomerPaymentInvoiceGroup[]>([]);
  const [searchedCustomer, setSearchedCustomer] =
    useState<CustomerPaymentSearchCustomer | null>(null);
  const [paymentsToCreate, setPaymentsToCreate] = useState<
    CustomerPaymentDraftRow[]
  >([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectedInvoiceCode, setSelectedInvoiceCode] = useState<string | null>(
    null,
  );
  const [deletingPaymentIds, setDeletingPaymentIds] = useState<Set<string>>(
    () => new Set(),
  );

  const groupsByInvoice = useMemo(
    () => new Map(groups.map((group) => [group.invoiceCode, group])),
    [groups],
  );

  // The opening/resulting balances are group-level values (not sums of the
  // leaf payments), so we expose them through custom aggregation functions.
  // This gives each group row a real numeric value that the grid can sort and
  // filter on, instead of an undefined cell.
  const aggregationFunctions = useMemo(() => {
    const groupValue = (
      selector: (group: CustomerPaymentInvoiceGroup) => number,
    ): GridAggregationFunction<number, number | null> => ({
      label: '',
      columnTypes: ['number'],
      apply: ({ groupId }) => {
        const node = apiRef.current.getRowNode(groupId) as {
          type?: string;
          groupingKey?: unknown;
        } | null;
        if (node?.type !== 'group' || node.groupingKey == null) return null;
        const group = groupsByInvoice.get(String(node.groupingKey));
        return group ? selector(group) : null;
      },
    });

    return {
      ...GRID_AGGREGATION_FUNCTIONS,
      sum: {
        ...GRID_AGGREGATION_FUNCTIONS.sum,
        label: '',
      },
      invoiceOpeningBalance: groupValue((group) => group.invoiceBalance),
      invoiceResultingBalance: groupValue((group) => group.resultingBalance),
    };
  }, [apiRef, groupsByInvoice]);

  const selectedInvoice = selectedInvoiceCode
    ? groupsByInvoice.get(selectedInvoiceCode) ?? null
    : null;
  const dateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  const allPayments = useMemo(
    () => groups.flatMap((group) => group.payments),
    [groups],
  );
  const totalPayments =
    allPayments.reduce((sum, payment) => sum + payment.payment, 0) +
    paymentsToCreate.reduce((sum, payment) => sum + payment.payment, 0);
  const totalAdjustments =
    allPayments.reduce((sum, payment) => sum + payment.adjustment, 0) +
    paymentsToCreate.reduce((sum, payment) => sum + payment.adjustment, 0);

  const customerDisplayName = searchedCustomer
    ? searchedCustomer.contactName
      ? `${searchedCustomer.companyName} - ${searchedCustomer.contactName}`
      : searchedCustomer.companyName
    : '';

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (!customer) {
      setGroups([]);
      setNextAfterKey(undefined);
      setSearchedCustomer(null);
      setPaymentsToCreate([]);
      setRowModesModel({});
      setSelectedInvoiceCode(null);
    }
  };

  const openBrowseCustomerModal = () => {
    dispatch(
      openSearchCustomerModal({
        onImport: (rows) => {
          const c = rows[0];
          if (c) setSelectedCustomer(c);
        },
        onCancel: () => {},
        multiSelect: false,
      }),
    );
  };

  const handleSearch = useCallback(async () => {
    if (dateRangeInvalid) return;
    if (searchBy === 'customerCode') {
      if (!selectedCustomer?.code?.trim()) return;
    } else {
      if (!searchInput.trim()) return;
    }
    setLoading(true);
    setSelectedInvoiceCode(null);
    try {
      const query =
        searchBy === 'customerCode' ? selectedCustomer!.code : searchInput;
      const payload: CustomerPaymentGroupedSearchInput = {
        query,
        searchBy,
        size: PAYMENT_GROUP_PAGE_SIZE,
        ...(searchBy === 'customerCode' && dateFrom
          ? { dateFrom: new Date(dateFrom).getTime() / 1000 }
          : {}),
        ...(searchBy === 'customerCode' && dateTo
          ? { dateTo: new Date(dateTo).getTime() / 1000 }
          : {}),
      };
      const response =
        await client.searchCustomerPaymentsGrouped.query(payload);
      setGroups(response.groups);
      setNextAfterKey(response.afterKey);
      setSearchedCustomer(response.customer ?? null);
    } catch (error) {
      console.error('Error searching grouped customer payments', error);
      setGroups([]);
      setNextAfterKey(undefined);
      setSearchedCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [
    selectedCustomer,
    searchInput,
    searchBy,
    dateFrom,
    dateTo,
    dateRangeInvalid,
  ]);

  const handleLoadMoreGroups = useCallback(async () => {
    if (!nextAfterKey || loading || loadingMore || dateRangeInvalid) return;
    if (searchBy === 'customerCode') {
      if (!selectedCustomer?.code?.trim()) return;
    } else {
      if (!searchInput.trim()) return;
    }

    setLoadingMore(true);
    try {
      const query =
        searchBy === 'customerCode' ? selectedCustomer!.code : searchInput;
      const payload: CustomerPaymentGroupedSearchInput = {
        query,
        searchBy,
        size: PAYMENT_GROUP_PAGE_SIZE,
        afterKey: nextAfterKey,
        ...(searchBy === 'customerCode' && dateFrom
          ? { dateFrom: new Date(dateFrom).getTime() / 1000 }
          : {}),
        ...(searchBy === 'customerCode' && dateTo
          ? { dateTo: new Date(dateTo).getTime() / 1000 }
          : {}),
      };
      const response =
        await client.searchCustomerPaymentsGrouped.query(payload);
      let addedCount = 0;
      setGroups((prev) => {
        const invoiceCodes = new Set(prev.map((group) => group.invoiceCode));
        const newGroups = response.groups.filter(
          (group) => !invoiceCodes.has(group.invoiceCode),
        );
        addedCount = newGroups.length;
        return [...prev, ...newGroups];
      });
      // Stop paginating when the backend has nothing more to give us.
      const reachedEnd =
        addedCount === 0 ||
        response.groups.length < PAYMENT_GROUP_PAGE_SIZE ||
        !response.afterKey;
      setNextAfterKey(reachedEnd ? undefined : response.afterKey);
      setSearchedCustomer((prev) => prev ?? response.customer ?? null);
    } catch (error) {
      console.error('Error loading more grouped customer payments', error);
      dispatch(
        pushToast({
          message: t('Unable to load more payments'),
          type: 'error',
          duration: 7000,
        }),
      );
    } finally {
      setLoadingMore(false);
    }
  }, [
    nextAfterKey,
    loading,
    loadingMore,
    dateRangeInvalid,
    searchBy,
    selectedCustomer,
    searchInput,
    dateFrom,
    dateTo,
    dispatch,
    t,
  ]);

  // `onRowsScrollEnd` is unreliable with row grouping (it often never fires
  // when the collapsed groups already fit in the viewport), so we attach our
  // own scroll listener to the grid's virtual scroller instead.
  React.useEffect(() => {
    const root = apiRef.current?.rootElementRef?.current;
    if (!root) return;

    const scroller = root.querySelector<HTMLDivElement>(
      '.MuiDataGrid-virtualScroller',
    );
    if (!scroller) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      if (scrollHeight - scrollTop - clientHeight < 120) {
        handleLoadMoreGroups();
      }
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [apiRef, handleLoadMoreGroups, groups.length, searchedCustomer]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleRecord = useCallback(async () => {
    // TODO: open a form dialog for recording a new payment or submit selected rows
    console.info('Record button clicked');
  }, []);

  const handleNew = useCallback(() => {
    if (!searchedCustomer) return;
    const id = `draft-${uuidv4()}`;
    const newRow: CustomerPaymentDraftRow = {
      id,
      isNew: true,
      customerCode: searchedCustomer.customerCode,
      companyName: searchedCustomer.companyName,
      invoiceCode: '',
      payment: 0,
      reference: '',
      adjustment: 0,
      detail: '',
      captureDate: Math.floor(Date.now() / 1000),
    };
    setPaymentsToCreate((prev) => [...prev, newRow]);
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  }, [searchedCustomer]);

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleSelectInvoice = useCallback((invoiceCode: string) => {
    setSelectedInvoiceCode(invoiceCode);
  }, []);

  const getGroupRowNode = useCallback(
    (id: GridRowId) =>
      apiRef.current.getRowNode(id) as {
        type?: string;
        groupingKey?: unknown;
        childrenExpanded?: boolean;
      } | null,
    [apiRef],
  );

  const getInvoiceCodeFromGroupRow = useCallback(
    (id: GridRowId) => {
      const rowNode = getGroupRowNode(id);

      if (rowNode?.type !== 'group' || rowNode.groupingKey == null) {
        return null;
      }

      return String(rowNode.groupingKey);
    },
    [getGroupRowNode],
  );

  const captureExpandedInvoiceCodes = useCallback(() => {
    const expandedInvoiceCodes = new Set<string>();

    apiRef.current.getAllRowIds().forEach((id) => {
      const rowNode = getGroupRowNode(id);
      if (
        rowNode?.type === 'group' &&
        rowNode.childrenExpanded &&
        rowNode.groupingKey != null
      ) {
        expandedInvoiceCodes.add(String(rowNode.groupingKey));
      }
    });

    expandedInvoiceCodesRef.current = expandedInvoiceCodes;
  }, [apiRef, getGroupRowNode]);

  const restoreExpandedInvoiceCodes = useCallback(() => {
    const expandedInvoiceCodes = expandedInvoiceCodesRef.current;
    if (expandedInvoiceCodes.size === 0) return;

    apiRef.current.getAllRowIds().forEach((id) => {
      const rowNode = getGroupRowNode(id);
      if (
        rowNode?.type === 'group' &&
        rowNode.groupingKey != null &&
        expandedInvoiceCodes.has(String(rowNode.groupingKey))
      ) {
        apiRef.current.setRowChildrenExpansion(id, true);
      }
    });
  }, [apiRef, getGroupRowNode]);

  const handleDeleteClick = (id: GridRowId) => () => {
    setPaymentsToCreate((prev) => prev.filter((row) => row.id !== id));
    setRowModesModel((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleDeletePayment = useCallback(
    async (row: PaymentTableRow) => {
      if (row.isNew || deletingPaymentIds.has(row.id)) return;

      setDeletingPaymentIds((prev) => new Set(prev).add(row.id));
      try {
        await client.deleteCustomerPayment.mutate({
          CustomerPaymentId: row.id,
        });
        captureExpandedInvoiceCodes();
        setGroups((prev) =>
          prev.flatMap((group) => {
            if (group.invoiceCode !== row.invoiceCode) return [group];

            const payments = group.payments.filter(
              (payment) => payment.CustomerPaymentId !== row.id,
            );
            if (payments.length === 0) return [];

            const totalPayment = group.totalPayment - row.payment;
            const totalAdjustment = group.totalAdjustment - row.adjustment;

            return [
              {
                ...group,
                payments,
                totalPayment,
                totalAdjustment,
                resultingBalance:
                  group.invoiceBalance - totalPayment - totalAdjustment,
              },
            ];
          }),
        );
        requestAnimationFrame(restoreExpandedInvoiceCodes);
        dispatch(
          pushToast({
            message: t('Payment for invoice {{invoiceCode}} deleted', {
              invoiceCode: row.invoiceCode,
            }),
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        console.error('Error deleting customer payment', error);
        dispatch(
          pushToast({
            message: t('Unable to delete payment for invoice {{invoiceCode}}', {
              invoiceCode: row.invoiceCode,
            }),
            type: 'error',
            duration: 7000,
          }),
        );
      } finally {
        setDeletingPaymentIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    },
    [
      deletingPaymentIds,
      dispatch,
      captureExpandedInvoiceCodes,
      restoreExpandedInvoiceCodes,
      t,
    ],
  );

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleRowModesModelChange = (newModel: GridRowModesModel) => {
    setRowModesModel(newModel);
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: true } as CustomerPaymentDraftRow;
    setPaymentsToCreate((prev) =>
      prev.map((row) => (row.id === newRow.id ? updatedRow : row)),
    );
    return updatedRow;
  };

  const numberTypeController = (value: any) =>
    value < 0 ? 0 : parseFloat(value);

  const tableRows = useMemo<PaymentTableRow[]>(() => {
    const invoiceRows: PaymentTableRow[] = groups.flatMap((group) => {
      if (group.payments.length === 0) {
        return [
          {
            id: `empty-invoice-${group.invoiceCode}`,
            isNew: false,
            isEmptyInvoice: true,
            customerCode: searchedCustomer?.customerCode ?? '',
            companyName: searchedCustomer?.companyName ?? '',
            invoiceCode: group.invoiceCode,
            invoiceDate: group.invoiceDate,
            openingBalance: group.invoiceBalance,
            payment: 0,
            reference: '',
            adjustment: 0,
            detail: '',
            captureDate: 0,
            resultingBalance: group.resultingBalance,
            groupInvoiceBalance: group.invoiceBalance,
            groupResultingBalance: group.resultingBalance,
          },
        ];
      }

      return group.payments.map(
        (payment): PaymentTableRow => ({
          id: payment.CustomerPaymentId,
          ...payment,
          isNew: false,
          groupInvoiceBalance: group.invoiceBalance,
          groupResultingBalance: group.resultingBalance,
        }),
      );
    });

    return [
      ...invoiceRows,
      ...paymentsToCreate.map((payment) => ({
        ...payment,
        invoiceDate: 0,
        openingBalance: 0,
        resultingBalance: 0,
        isNew: true,
      })),
    ];
  }, [groups, paymentsToCreate, searchedCustomer]);

  const renderGroupAwareCell = useCallback(
    (
      params: GridRenderCellParams<PaymentTableRow>,
      renderLeaf: (row: PaymentTableRow) => React.ReactNode,
    ) => {
      if (params.rowNode.type === 'group') {
        const group = groupsByInvoice.get(String(params.rowNode.groupingKey));
        if (!group) return null;

        switch (params.field) {
          case 'invoiceDate':
            return formatDate(group.invoiceDate);
          case 'openingBalance':
            return formatCurrency(group.invoiceBalance);
          case 'resultingBalance':
            return formatCurrency(group.resultingBalance);
          default:
            return null;
        }
      }

      return params.row.isEmptyInvoice ? null : renderLeaf(params.row);
    },
    [groupsByInvoice],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'customerCode',
        headerName: t('Company Code'),
        width: 130,
      },
      {
        field: 'companyName',
        headerName: t('Company Name'),
        width: 180,
      },
      {
        field: 'invoiceCode',
        headerName: t('Invoice No.'),
        width: 170,
        editable: true,
      },
      {
        field: 'invoiceDate',
        headerName: t('Invoice Date'),
        width: 120,
        renderCell: (params) =>
          renderGroupAwareCell(params, (row) => formatDate(row.invoiceDate)),
      },
      {
        field: 'openingBalance',
        headerName: t('Opening Balance'),
        width: 140,
        type: 'number',
        renderCell: (params) =>
          renderGroupAwareCell(params, (row) =>
            formatCurrency(row.openingBalance),
          ),
      },
      {
        field: 'payment',
        headerName: t('Payment'),
        width: 110,
        type: 'number',
        editable: true,
        aggregable: true,
        valueParser: numberTypeController,
        valueFormatter: (value: number) => formatCurrency(value),
        renderCell: (params) =>
          params.row.isEmptyInvoice ? null : formatCurrency(params.row.payment),
      },
      {
        field: 'reference',
        headerName: t('Reference'),
        width: 130,
        editable: true,
      },
      {
        field: 'adjustment',
        headerName: t('Adjustment'),
        width: 120,
        type: 'number',
        editable: true,
        aggregable: true,
        valueParser: numberTypeController,
        valueFormatter: (value: number) => formatCurrency(value),
        renderCell: (params) =>
          params.row.isEmptyInvoice
            ? null
            : formatCurrency(params.row.adjustment),
      },
      {
        field: 'detail',
        headerName: t('Detail'),
        width: 150,
        editable: true,
      },
      {
        field: 'captureDate',
        headerName: t('Date'),
        width: 140,
        editable: true,
        type: 'date',
        valueGetter: (value: number) => (value ? new Date(value * 1000) : null),
        valueSetter: (value: Date | null, row: any) => ({
          ...row,
          captureDate: value ? Math.floor(value.getTime() / 1000) : 0,
        }),
      },
      {
        field: 'resultingBalance',
        headerName: t('Resulting Balance'),
        width: 150,
        type: 'number',
        renderCell: (params) =>
          renderGroupAwareCell(params, (row) =>
            formatCurrency(row.resultingBalance),
          ),
      },
      {
        field: 'actions',
        headerName: t('Actions'),
        width: 80,
        cellClassName: 'actions',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        display: 'flex',
        renderCell: (params) => {
          const invoiceCode = getInvoiceCodeFromGroupRow(params.id);

          if (invoiceCode) {
            return (
              <InvoiceGroupActionButton
                invoiceCode={invoiceCode}
                onSelect={handleSelectInvoice}
              />
            );
          }

          if (params.row.isEmptyInvoice) {
            return null;
          }

          if (!params.row.isNew) {
            return (
              <PaymentRowActionButton
                disabled={deletingPaymentIds.has(params.row.id)}
                onDelete={() => handleDeletePayment(params.row)}
              />
            );
          }

          const isInEditMode =
            rowModesModel[params.id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  aria-label={t('Save')}
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSaveClick(params.id)();
                  }}
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label={t('Cancel')}
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCancelClick(params.id)();
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          }

          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                aria-label={t('Edit')}
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditClick(params.id)();
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label={t('Delete')}
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteClick(params.id)();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [
      rowModesModel,
      renderGroupAwareCell,
      handleSelectInvoice,
      getInvoiceCodeFromGroupRow,
      deletingPaymentIds,
      handleDeletePayment,
      t,
    ],
  );

  return (
    <div className="flex flex-row w-full min-w-0 overflow-hidden mx-10 my-12 md:mx-20 flex-1 gap-4">
      <div className="flex flex-col flex-1 min-w-0 gap-4">
        {/* Search/Filter Section */}
        <div className="mx-4 mb-2 p-4 border border-gray-200 border-solid rounded shadow-xl">
          <h2 className="text-blue-600">{t('Payment Received')}</h2>
          <hr />
          <Box
            className="mt-5"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 3,
              height: 76,
            }}
          >
            <FormControl>
              <FormLabel>{t('Search by')}</FormLabel>
              <RadioGroup
                row
                value={searchBy}
                onChange={(e) => {
                  setSearchBy(e.target.value as 'customerCode' | 'invoiceCode');
                  setGroups([]);
                  setNextAfterKey(undefined);
                  setSearchedCustomer(null);
                  setSearchInput('');
                  setSelectedCustomer(null);
                  setPaymentsToCreate([]);
                  setRowModesModel({});
                  setSelectedInvoiceCode(null);
                }}
              >
                <FormControlLabel
                  value="customerCode"
                  control={<Radio size="small" />}
                  label={t('Company')}
                />
                <FormControlLabel
                  value="invoiceCode"
                  control={<Radio size="small" />}
                  label={t('Invoice No.')}
                />
              </RadioGroup>
            </FormControl>

            {searchBy === 'customerCode' ? (
              <Stack sx={{ minWidth: 280 }} gap={0.5}>
                <SelectSearchCustomer
                  label={t('Customer')}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Link
                    underline="always"
                    sx={{ cursor: 'pointer' }}
                    onClick={openBrowseCustomerModal}
                  >
                    {t('Browse Customer')}
                  </Link>
                </Box>
              </Stack>
            ) : (
              <TextField
                size="small"
                label={t('Search by invoice number...')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {searchBy === 'customerCode' && (
              <>
                <TextField
                  size="small"
                  label={t('From')}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  error={dateRangeInvalid}
                  helperText={
                    dateRangeInvalid ? t('From must be before To') : ''
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: dateTo || undefined }}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  label={t('To')}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  error={dateRangeInvalid}
                  helperText={
                    dateRangeInvalid ? t('To must be after From') : ''
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: dateFrom || undefined }}
                  sx={{ width: 160 }}
                />
              </>
            )}

            <Button
              variant="outlined"
              color="primary"
              disabled={dateRangeInvalid}
              onClick={handleSearch}
            >
              {t('Search')}
            </Button>
          </Box>
        </div>

        {/* Data Table */}
        <div className="mx-4 flex flex-col flex-1 min-w-0 overflow-hidden border border-gray-200 border-solid rounded shadow-xl">
          {searchedCustomer ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                px: 2,
                py: 3,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <Chip
                    label={searchedCustomer.customerCode}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 400 }}
                  />
                  <Typography variant="h5">{customerDisplayName}</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mt: 1,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {searchedCustomer.email && (
                    <>
                      <EmailOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                      <Link
                        href={`mailto:${searchedCustomer.email}`}
                        underline="always"
                        color="text.secondary"
                        variant="body1"
                      >
                        {searchedCustomer.email}
                      </Link>
                    </>
                  )}
                  {searchedCustomer.email && searchedCustomer.phone && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mx: 0.5, borderColor: 'divider' }}
                    />
                  )}
                  {searchedCustomer.phone && (
                    <>
                      <PhoneOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                      <Typography variant="body1" color="text.secondary">
                        {searchedCustomer.phone}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box height={`95px`} />
          )}
          <div
            style={{
              height: `calc(100vh - ${topNavBarHeight} - 450px)`,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <DataGridPremium
              apiRef={apiRef}
              rows={tableRows}
              columns={columns}
              loading={loading}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              isCellEditable={(params) =>
                params.row.isNew === true && !params.row.isEmptyInvoice
              }
              onRowDoubleClick={(params) => {
                const invoiceCode = getInvoiceCodeFromGroupRow(params.id);
                if (invoiceCode) {
                  handleSelectInvoice(invoiceCode);
                }
              }}
              onRowsScrollEnd={handleLoadMoreGroups}
              disableRowSelectionOnClick
              rowGroupingModel={['invoiceCode']}
              groupingColDef={{
                headerName: t('Invoice No.'),
                width: 180,
                hideDescendantCount: true,
                sortComparator: (a, b) => Number(a) - Number(b),
                valueFormatter: (value) =>
                  value != null && value !== '' ? `${t('INV')} ${value}` : '',
              }}
              aggregationFunctions={aggregationFunctions}
              defaultGroupingExpansionDepth={0}
              hideFooter={true}
              columnHeaderHeight={56}
              pinnedColumns={{ right: ['actions'] }}
              sx={{
                flex: 1,
                width: '100%',
                minHeight: 0,
                minWidth: 0,
                border: 'none',
                padding: '1rem',
                '.MuiDataGrid-row:nth-of-type(even)': {
                  backgroundColor: '#ECECEC',
                },
                '.MuiDataGrid-row:nth-of-type(even).MuiDataGrid-row--grouping':
                  {
                    backgroundColor: '#E3EEF9',
                  },
                '.MuiDataGrid-row.MuiDataGrid-row--grouping': {
                  backgroundColor: '#E3EEF9',
                  fontWeight: 600,
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    invoiceCode: false,
                  },
                },
                rowGrouping: {
                  model: ['invoiceCode'],
                },
                sorting: {
                  sortModel: [{ field: 'captureDate', sort: 'desc' }],
                },
                aggregation: {
                  model: {
                    payment: 'sum',
                    adjustment: 'sum',
                    openingBalance: 'invoiceOpeningBalance',
                    resultingBalance: 'invoiceResultingBalance',
                  },
                },
                pagination: {
                  paginationModel: { pageSize: 100 },
                },
              }}
              pageSizeOptions={[100]}
              slots={{
                loadingOverlay: () => <LinearProgress color="primary" />,
              }}
            />
            {loadingMore ? (
              <Box
                sx={{
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : null}
          </div>
        </div>
      </div>

      <DraftPaymentsSidebar
        selectedInvoice={selectedInvoice}
        searchedCustomer={searchedCustomer}
        onDraftCreated={() => setSelectedInvoiceCode(null)}
      />
    </div>
  );
}
